// @flow
import React from 'react';
import { get, find, pick } from 'lodash';
import URI from 'urijs';
import { windowWrapper } from '@clubmed/utils';

import { withRouter } from 'Client/router/withRouter';
import ReactAppContext from 'Context/ReactAppContext';
import FormValidator from 'Components/frameworks/form-validator-hoc/FormValidator.jsx';

import { COLORS } from 'Components/utils/color/colors';

import { Fieldset } from 'Components/frameworks/fieldset/Fieldset.jsx';
import { Title } from 'Components/frameworks/title/Title.jsx';
import ParticipantsErrors from 'Client/cms/toolkit/product-booking/participantsErrors.jsx';
import {
  DEFAULT_MIN_AGE,
  DEFAULT_MAX_AGE,
} from 'Components/widgets/trip-information/participants/participants.constants';
import { BookingErrors } from 'Components/widgets/booking-errors/BookingErrors.jsx';
import { isFeatureFlipped } from 'Utils/feature-flipping/featureFlippingUtils';
import { isSeller } from 'Utils/seller/sellerUtils';
import { hasFetchProposalsFailed } from 'Utils/proposal/proposalUtils';
import {
  PAGE_BE_ACCOMMODATION,
  getBookingEngineUri,
} from 'Utils/booking-engine-pages/BookingPagesUtils';
import { QUERY_TRANSPORT } from 'Utils/uri/constants';
import redirectUtils from 'Utils/uri/redirectUtils';
import { getSpecificQueryParam } from 'Utils/uri/uriUtils';
import { getFormattedKidsBirthdate } from 'Utils/trip-information/tripInformationUtils';
import ParticipantsValidator from 'Client/cms/toolkit/product-booking/participants.validator';
import type { FormErrorsType } from 'Client/cms/toolkit/product-booking/participants.validator';
import { CruiseFooter } from 'Components/widgets/cruise-booking/CruiseFooter.jsx';
import { CruiseParticipants } from 'Components/widgets/cruise-booking/CruiseParticipants.jsx';
import { CruiseCriteriaFieldset } from 'Components/widgets/cruise-booking/CruiseCriteriaFieldset.jsx';

import legacyOpener from 'Utils/legacy-booking-engine/legacyOpener';
import { type ContainerProps } from 'Components/widgets/cruise-booking/CruiseBooking.container';
import { callPrice, type PriceType } from 'Components/widgets/cruise-booking/CruiseBooking.utils';
import {
  type LineProps,
  type ResolvedProps,
} from 'Client/cms/toolkit/cruise-booking/cruiseBooking.resolver';
import { BOOKING_ENGINE_ENTRIES } from 'Store/booking/constants';

type Props = {
  ...$Exact<ResolvedProps>,
  ...$Exact<LineProps>,
  ...$Exact<ContainerProps>,
  history: RouterHistory,
  location: RouterLocation<{}, {}>,
};

export const DEFAULT_DATE_FORMAT = 'DD/MM/YYYY';

export class _CruiseBooking extends FormValidator {
  static contextType = ReactAppContext;
  static defaultProps = {
    adultsNumber: 2,
    kids: [],
    resortId: '',
  };

  constructor(props: Props) {
    super(props);

    this.state = {
      loading: true,
      price: {},
      fields: [],
      hasBeenSubmitted: false,
      transport: '',
      proposalError: '',
    };
  }

  componentDidMount() {
    this.updatePrice();
  }

  static getDerivedStateFromProps(props: Props, state: *) {
    if (getSpecificQueryParam(QUERY_TRANSPORT) !== state.transport) {
      return {
        ...state,
        loading: true,
        transport: getSpecificQueryParam(QUERY_TRANSPORT),
      };
    }
    return null;
  }

  componentDidUpdate(prevProps: Props, prevState: *) {
    if (prevState && prevState.transport !== this.state.transport) {
      this.updatePrice();
    }
  }

  goToLegacyBookingEngine = (
    bookingParams: {
      adults: number,
      children: number,
      birthdays: Array<string>,
      duration: number,
      arrivalDate: string,
      resortId: string,
      transport: string,
    },
    birthDateFormat: string,
  ) => {
    legacyOpener.openLegacy(this.context, bookingParams, birthDateFormat);
  };

  handleTransport = (value: string): void => {
    const redirectUri = new URI(windowWrapper.getLocation())
      .setSearch(QUERY_TRANSPORT, value)
      .toString();
    redirectUtils.redirectTo()(redirectUri);
  };

  redirectToLegacyBookingEngine = (): void => {
    const dateFormat = get(this.props, 'dateFormat', DEFAULT_DATE_FORMAT);
    const { resortId, adultsNumber, kids, cruise } = this.props;
    const { price, transport } = this.state;

    let arrivalDate = get(price, 'termsAndConditions.arrivalDate');
    if (!arrivalDate) {
      // If the `/v0/search_price` API do not send back a price (e.g. when there is a transport added),
      // the `termsAndConditions.arrivalDate` is empty.
      // In that case, we fallback to the `departureDate` of the cruise - as the parameter
      // `arrivalDate` means "arrival on the site" and _not_ the end of the cruise (#8962).
      arrivalDate = cruise.departureDate;
    }

    const duration = get(price, 'termsAndConditions.duration');

    this.goToLegacyBookingEngine(
      {
        adults: adultsNumber,
        children: kids.length,
        birthdays: getFormattedKidsBirthdate(kids, dateFormat),
        duration,
        arrivalDate,
        resortId,
        transport,
      },
      dateFormat,
    );
  };

  redirectToBookingEngine = async () => {
    const {
      cruise,
      product,
      adultsNumber,
      kids,
      simpleFetchProposals,
      location,
      getBookingEngineI18n,
    } = this.props;

    let arrivalDate = get(this.state.price, 'termsAndConditions.arrivalDate');
    if (!arrivalDate) {
      // If the `/v0/search_price` API do not send back a price (e.g. when there is a transport added), the `termsAndConditions.arrivalDate` is empty. In that case, we fallback to the `departureDate` of the cruise - as the parameter `arrivalDate` means "arrival on the site" and _not_ the end of the cruise (#8962).
      arrivalDate = cruise.departureDate;
    }

    const criterias = {
      productId: product.id,
      arrivalDate,
      duration: get(this.state.price, 'termsAndConditions.duration'),
      attendees: {
        adults: adultsNumber,
        kids,
      },
      departureCity: getSpecificQueryParam(QUERY_TRANSPORT) || 'NO',
      bookingEngineEntry: BOOKING_ENGINE_ENTRIES.CRUISE_BOOKING,
    };

    const [{ proposals }] = await Promise.all([
      simpleFetchProposals(
        criterias,
        get(this.props, 'config.booking.form.proposalsLoaderLabel', 'loading'),
        this.context,
        location,
      ),
      getBookingEngineI18n(this.context),
    ]);

    const failedProposals = hasFetchProposalsFailed(proposals);

    if (!failedProposals) {
      this.props.history.pushRoute(
        getBookingEngineUri(
          PAGE_BE_ACCOMMODATION,
          proposals.proposals,
          proposals.resort,
          proposals.proposals[0].id,
          location.queryParams,
        ),
      );
    } else {
      this.setState({ proposalError: proposals.errors });
    }
  };

  updatePrice(): void {
    callPrice({
      departureCity: this.state.transport,
      appContext: this.context,
      location: this.props.location,
      productId: this.props.product.id,
    }).then(({ price }: PriceType) => {
      this.setState({
        loading: false,
        price,
      });
    });
  }

  getFieldByName(name: string): * {
    return find(this.state.fields, { name });
  }

  checkFormErrors(): FormErrorsType {
    const { dateFormat = DEFAULT_DATE_FORMAT } = this.props;
    const birthdays = getFormattedKidsBirthdate(this.props.kids, dateFormat);
    const adultAgeInYear = +get(this.props, 'config.booking.form.adultAge', DEFAULT_MAX_AGE);
    const childAgeInYear = +get(this.props, 'product.minAuthorizedAge.value', DEFAULT_MIN_AGE) / 12;
    const minAgeErrorMessage = get(this.props, 'product.minAgeErrorMessage.formattedValue');

    const { adultsNumber = 0, kids, cruise = {} } = this.props;

    return ParticipantsValidator.checkFormErrors({
      adults: adultsNumber,
      children: kids.length,
      departureDate: cruise.departureDate,
      birthdays,
      dateFormat,
      adultAge: adultAgeInYear,
      childAge: childAgeInYear,
      birthdayFormatErrorMessage: get(this.props, 'config.booking.form.errors.birthdayFormat'),
      adultAgeErrorMessage: get(this.props, 'config.booking.form.errors.adultChild'),
      minAgeErrorMessage,
    });
  }

  handleSubmit = (e: SyntheticEvent<>) => {
    e.preventDefault();
    this.setState({ hasBeenSubmitted: true, proposalError: '' }, () => {
      const haveError = Object.values(this.checkFormErrors()).some(error => error);
      if (haveError) return;

      const featureFlipSeller = isFeatureFlipped(this.context)(
        'seller.booking.cruise.redirectToBEResponsive',
      );
      if (isSeller(this.context) && featureFlipSeller) return this.redirectToBookingEngine();

      const featureFlipClient = isFeatureFlipped(this.context)(
        'booking.cruise.redirectToBEResponsive',
      );
      if (!isSeller(this.context) && featureFlipClient) return this.redirectToBookingEngine();

      return this.redirectToLegacyBookingEngine();
    });
  };

  render() {
    const { loading, price, transport } = this.state;
    const {
      adultsNumber,
      bookingCriteria,
      config,
      criteria,
      cruise,
      dateFormat,
      kids,
      pax = {},
      product,
      setAdultsNumber,
      setKids,
      title,
    } = this.props;

    return (
      <React.Fragment>
        <div className="PriceTicket-sticky-beacon" />
        <section id="booking" className="py-10">
          <form data-testid="CruiseBookingForm-form" onSubmit={this.handleSubmit}>
            <Title
              data-testid="CruiseBookingForm-title"
              color={COLORS.GRAY_DARKER}
              className="mb-8"
              theme="title-sans-4"
            >
              {title}
            </Title>
            <Fieldset
              className="mb-12"
              legend={criteria.title}
              data-testid="CruiseBookingForm-criteriaFieldset"
            >
              <div className="sm:flex sm:flex-wrap -m-2">
                <CruiseCriteriaFieldset
                  bookingCriteria={bookingCriteria}
                  cruise={cruise}
                  product={product}
                  transport={transport}
                  onChange={this.handleTransport}
                />
              </div>
            </Fieldset>
            <Fieldset
              className="mb-12"
              legend={pax.title}
              data-testid="CruiseBookingForm-paxFieldset"
            >
              <CruiseParticipants
                adultsNumber={adultsNumber}
                cruise={cruise}
                dateFormat={dateFormat}
                kids={kids}
                product={product}
                setAdultsNumber={setAdultsNumber}
                setKids={setKids}
              />

              {this.state.proposalError && (
                <BookingErrors
                  data-testid="cruiseBookingForm-Package-errorContent"
                  errorCode={this.state.proposalError[0]?.errorCode}
                />
              )}

              <ParticipantsErrors
                config={pick(config.booking.form, [
                  'errorMaxGuestsLabel',
                  'departureDateErrorMessage',
                ])}
                errors={this.checkFormErrors()}
                isSubmitted={this.state.hasBeenSubmitted}
              />
            </Fieldset>
            <CruiseFooter
              config={config}
              loading={loading}
              price={price}
              onClick={this.handleSubmit}
            />
          </form>
        </section>
      </React.Fragment>
    );
  }
}

export const CruiseBooking = withRouter(_CruiseBooking);
